import { useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { clearEmailValidation, validateEmail } from '../store/slices/user';
import { useAppSelector } from './useAppSelector';
import { useAppDispatch } from './useAppDispatch';
import { showSingletonToast } from 'utils/toastSingleton';
import { RootState } from '../store';

interface EmailVerifierParams {
    hash: string;
}

const useEmailVerifier = () => {
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { hash } = useParams<EmailVerifierParams>();
    const user = useAppSelector((state) => state.user);
    const isComplete = useAppSelector((state: RootState) => state.auth.tokenData?.data?.isComplete);

    const { validatingEmail, validatingEmailError, validatingEmailSuccess } = user;
    const hasHandledResult = useRef(false);

    useEffect(() => {
        if (hash) {
            showSingletonToast('Validating email...', { isLoading: true, autoClose: false });
            void dispatch(validateEmail({ hash }));
        }
    }, [hash, dispatch]);

    useEffect(() => {
        if (!validatingEmail && !hasHandledResult.current) {
            hasHandledResult.current = true;

            if (validatingEmailSuccess) {
                showSingletonToast('Email successfully verified.', {
                    type: 'success',
                    isLoading: false,
                    autoClose: 3000,
                });
                // Only redirect if account is already complete (has a password).
                // If not complete, stay on this page so EmailVerification can show the password form.
                if (isComplete) {
                    dispatch(clearEmailValidation());
                    history.push('/');
                }
            } else if (validatingEmailError) {
                showSingletonToast('Email validation error.', {
                    type: 'error',
                    isLoading: false,
                    autoClose: 3000,
                });
            }
        }
    }, [
        validatingEmail,
        validatingEmailSuccess,
        validatingEmailError,
        isComplete,
        dispatch,
        history,
    ]);
};

export default useEmailVerifier;
